"use client"

import { ControlledModal, toast } from "@/components"
import { TextInput } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import { UpdateIndividualCreativeDisplayNameDocument } from "@/gql/graphql"
import { useMutation } from "@apollo/client"
import { captureException } from "@sentry/nextjs"
import { FC, useState } from "react"

const EditNameModalFragment = graphql(`
  fragment EditNameModal on IndividualCreative {
    id
    __typename
    displayName

    user {
      id
      name
    }
  }
`)

interface Props {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  creative: FragmentType<typeof EditNameModalFragment> | null | undefined
}

graphql(`
  mutation UpdateIndividualCreativeDisplayName($input: UpdateIndividualCreativeDisplayNameInput!) {
    updateIndividualCreativeDisplayName(input: $input) {
      errors {
        field
        messages
      }
      individualCreative {
        __typename
        id
        displayName
      }
    }
  }
`)

export const EditNameModal: FC<Props> = ({ isOpen, onOpenChange, ...props }) => {
  const creative = useFragment(EditNameModalFragment, props.creative)
  const [loading, setLoading] = useState(false)
  const [update] = useMutation(UpdateIndividualCreativeDisplayNameDocument)
  const [value, setValue] = useState(creative?.displayName || creative?.user?.name || "")

  const onUpdate = async () => {
    if (loading) return
    setLoading(true)
    const response = await update({
      variables: {
        input: {
          id: creative?.id!,
          displayName: value,
        },
      },
    })

    const errors = response.data?.updateIndividualCreativeDisplayName?.errors
    setLoading(false)
    if (errors && errors.length > 0) {
      captureException(errors)
      toast.error("An error occurred. Please try again.")
    } else {
      onOpenChange(false)
    }
  }

  return (
    <ControlledModal
      open={isOpen}
      onOpenChange={onOpenChange}
      header={{ title: "Update your name", variant: "default" }}
      body={
        <div className="flex flex-1 flex-col">
          <TextInput
            label="Name"
            tabIndex={0}
            value={value}
            onChange={e => {
              setValue(e.target.value)
            }}
          />
        </div>
      }
      footer={{
        fixed: true,
        tertiaryButton: {
          children: "Cancel",
          onClick: () => onOpenChange(false),
        },
        primaryButton: {
          children: "Update",
          onClick: onUpdate,
          loading: loading,
        },
      }}
    />
  )
}
